import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { MaterialReader } from './StudySubjectMaterials';
import { getStudyDocument, startStudySession } from './studyApi';
import { useStudyAnalysis } from './useStudyAnalysis';
import { analysisPath } from './studyAnalysisApi';

/** A source owns its URL. Return targets stay inside the Study directory. */
export function StudyMaterialPage() {
  const owner = useAuthStore(() => getKidSub());
  const { documentId } = useParams();
  return owner && documentId ? (
    <MaterialPageForLearner key={`${owner}:${documentId}`} owner={owner} documentId={documentId} />
  ) : (
    <p className="p-6 text-ink">Sign in as a student to open your material.</p>
  );
}

function MaterialPageForLearner({ owner, documentId }: { owner: string; documentId: string }) {
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const proposedReturn = location.state?.returnTo;
  const returnTo =
    typeof proposedReturn === 'string' &&
    /^\/learn\/study(?:\/materials|\/subjects\/[a-z0-9-]+)?(?:\?[^#]*)?$/.test(proposedReturn)
      ? proposedReturn
      : '/learn/study';
  const source = useQuery({
    queryKey: ['study-document', owner, documentId],
    queryFn: () => getStudyDocument(documentId),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [readerDirty, setReaderDirty] = useState(false);
  const originalRequested = new URLSearchParams(location.search).get('original') === '1';
  const analysis = useStudyAnalysis({ documentId: originalRequested ? null : documentId });
  useEffect(() => {
    const current = analysis.data;
    if (!originalRequested && !readerDirty && current?.result?.learning_note_id && getKidSub() === owner)
      navigate(analysisPath(current.primary_document_id, current.id), {
        replace: true,
        state: location.state,
      });
  }, [analysis.data, originalRequested, readerDirty, owner, navigate, location.state]);
  const tutor = useMutation({
    mutationFn: () => startStudySession(documentId),
    onSuccess: (session) => {
      if (live.current && getKidSub() === owner) navigate(`/learn/study/sessions/${session.id}`);
    },
  });
  return (
    <MaterialReader
      document={source.isFetchedAfterMount && !source.isError ? source.data : undefined}
      loading={!source.isFetchedAfterMount && !source.isError}
      error={source.isError}
      starting={tutor.isPending}
      startError={tutor.error instanceof Error ? tutor.error.message : undefined}
      onRetry={() => void source.refetch()}
      onStudy={() => tutor.mutate()}
      onUnsavedChange={setReaderDirty}
      onClose={() =>
        navigate(returnTo, { state: { libraryFilters: location.state?.libraryFilters } })
      }
    />
  );
}
